import React from 'react'
import { connect } from 'react-redux'
import { makeStyles } from '@material-ui/core'
import { Button } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { CapeeshColors, HexToColorWithAlpha } from '../../assets/ColorPalette'
import { FormattedMessage } from 'react-intl'

const useStyles = makeStyles((theme) => ({
    main: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        height: 'calc(100vh - 60px)',
        gap: 32,
    },
    mainText: {
        color: CapeeshColors.Primary900,
        fontSize: 24,
        fontFamily: 'Rubik',
        fontWeight: '600',
        textAlign: 'center',
    },
    subText: {
        color: CapeeshColors.Gray900,
        fontSize: 18,
        fontFamily: 'Rubik',
        fontWeight: '400',
        textAlign: 'center',
    },
    appDownloadContainer: {
        backgroundColor: CapeeshColors.Gray4,
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        gap: 8,
    },
    appDownloadContainerTopText: {
        color: CapeeshColors.Primary900,
        fontSize: 24,
        fontFamily: 'Rubik',
        fontWeight: '600',
        textAlign: 'center',
        width: '100%',
    },
    appExplanationContainer: {
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        gap: 8,
    },
    appExplanationContainerTopText: {
        color: CapeeshColors.Primary900,
        fontSize: 24,
        fontFamily: 'Rubik',
        fontWeight: '600',
        textAlign: 'center',
        width: '100%',
    },
    appExplanationSubContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
    },
    appExplanationSubContainerNumber: {
        color: CapeeshColors.Primary900,
        fontSize: 32,
        fontFamily: 'Rubik',
        fontWeight: '1000',
    },
    appExplanationSubContainerText: {
        color: CapeeshColors.Gray900,
        fontSize: 18,
        fontFamily: 'Rubik',
        fontWeight: '400',
    },
    appButtonContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 8,
        justifyContent: 'center',
        justifyItems: 'center',
        alignItems: 'center',
        '@media (max-width: 400px)': {
            flexDirection: 'column',
        },
    },
}))

const StudentAppOnboardingPage = ({ onGoBakToCapeeshApp, studentEmail }) => {
    const classes = useStyles()

    const StoreLinks = () => {
        return (
            <div className={classes.appButtonContainer}>
                <a
                    href="https://play.google.com/store/apps/details?id=com.capeesh.personalized.language.learning"
                    target="_blank"
                    rel="noopener noreferrer">
                    <img
                        src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                        alt="Get it on Google Play"
                        style={{ height: '60px' }}
                    />
                </a>

                <a href="https://apps.apple.com/app/id1390963312" target="_blank" rel="noopener noreferrer">
                    <img
                        src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                        alt="Download on the App Store"
                        style={{ height: '60px' }}
                    />
                </a>
            </div>
        )
    }

    return (
        <div className={classes.main}>
            <div style={{ display: 'flex', alignItems: 'left', paddingLeft: '16px', paddingTop: '16px' }}>
                <Button
                    style={{ width: '250px', height: '40px', textTransform: 'none' }}
                    variant={'contained'}
                    onClick={onGoBakToCapeeshApp}>
                    <ArrowBackIcon width={25} height={25} style={{ paddingRight: '8px' }} />
                    <FormattedMessage
                        id={'student.back.to.capeesh.app'}
                        defaultMessage={'Back to {appName}'}
                        values={{ appName: 'Capeesh App' }}
                    />
                </Button>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 32,
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyItems: 'center',
                    height: '100%',
                }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 16,
                    }}>
                    <div className={classes.mainText}>
                        <FormattedMessage
                            id="student.app.onboarding.title"
                            defaultMessage={'Using the Capeesh phone app'}
                        />
                    </div>
                    <div className={classes.subText}>
                        <FormattedMessage
                            id="student.app.onboarding.top.sub.text"
                            defaultMessage={
                                'The Capeesh app can also be downloaded and played on a phone! Here is how to get it up and running:'
                            }
                        />
                    </div>
                </div>
                <div className={classes.appDownloadContainer}>
                    <div className={classes.appDownloadContainerTopText}>
                        <FormattedMessage
                            id={'student.app.onboarding.get.app.text'}
                            defaultMessage={'Get Capeesh now:'}
                        />
                    </div>
                    <div className={classes.appDownloadContainerContent}>
                        <StoreLinks />
                    </div>
                </div>
                <div className={classes.appExplanationContainer}>
                    <div className={classes.appExplanationContainerTopText}>
                        <FormattedMessage
                            id="student.app.onboarding.get.started.steps.top.text"
                            defaultMessage={'How to get started in the app:'}
                        />
                    </div>
                    <div className={classes.appExplanationSubContainer}>
                        <div className={classes.appExplanationSubContainerNumber}>1</div>
                        <div className={classes.appExplanationSubContainerText}>
                            <FormattedMessage
                                id="student.app.onboarding.step.one.text"
                                defaultMessage={
                                    'Open the Capeesh Language Learning App and select your native language.'
                                }
                            />
                        </div>
                    </div>
                    <div className={classes.appExplanationSubContainer}>
                        <div className={classes.appExplanationSubContainerNumber}>2</div>
                        <div className={classes.appExplanationSubContainerText}>
                            <FormattedMessage
                                id="student.app.onboarding.step.two.text"
                                defaultMessage={
                                    'Insert the username {studentEmail} to activate your new user, and create your own unique password.'
                                }
                                values={{ studentEmail: studentEmail }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

StudentAppOnboardingPage.propTypes = {}

const mapStateToProps = (state) => {
    return {}
}

const mapDispatchToProps = (dispatch) => {
    return {}
}

export default connect(mapStateToProps, mapDispatchToProps)(StudentAppOnboardingPage)
